import { GatsbyImage } from 'gatsby-plugin-image';
import { useContext } from 'react';

import { Container, Section } from 'components/GridV2';
import { Clock, ShortArrow } from 'components/icons';
import { LocaleContext } from 'components/rebrand/Layout';
import { Link } from 'components/rebrand/Link';
import { H1 } from 'components/rebrand/Typography';

import { IArticleSanityData } from 'typescriptDefs/pages/learn/article';

import { getSanityImageUrl } from 'helpers/sanityImageBuilder';

import { Authors } from './Authors';

type Props = {
  article: IArticleSanityData;
  isGROQ: boolean;
};

export const ArticleHeader = ({ article, isGROQ }: Props) => {
  const locale = useContext(LocaleContext);

  const image =
    isGROQ && article.img.url
      ? getSanityImageUrl(article.img.url, {
          width: 1376,
          height: 874,
        })
      : article.img.asset;

  const srcSet =
    article.img.url &&
    `${getSanityImageUrl(article.img.url, {
      width: 320,
      height: 203,
    })} 320w, ${getSanityImageUrl(article.img.url, {
      width: 654,
      height: 415,
    })} 654w, ${getSanityImageUrl(article.img.url, {
      width: 768,
      height: 488,
    })} 768w, ${getSanityImageUrl(article.img.url, {
      width: 1024,
      height: 650,
    })} 1024w, ${getSanityImageUrl(article.img.url, {
      width: 1376,
      height: 874,
    })} 1376`;

  return (
    <Section className="pb-64 pt-40 md:pb-64 md:pt-64">
      <Container className="px-32 xs:px-0">
        <div className="grid grid-cols-1 gap-64 md:grid-cols-2 md:gap-32">
          <div className="my-auto">
            <Link
              aria-label={{ en: `Back`, fr: `Retour` }[locale]}
              className="mb-32 flex w-fit flex-row items-center gap-8 text-grey-400 md:mb-48"
              href={locale === 'fr' ? '/fr/learn' : '/learn/'}
            >
              <ShortArrow className="h-24 w-24 rotate-180" />
              <p className="text-md font-medium leading-normal">{{ en: `Back`, fr: `Retour` }[locale]}</p>
            </Link>
            <H1 className="mb-32 text-primary-350 md:mb-48">{article.title}</H1>
            <div className="mb-32 flex flex-row items-center gap-12 text-grey-500 md:mb-48">
              <Clock className="h-24 w-24" />
              <p className="mb-0 text-md font-medium leading-normal">
                {
                  {
                    en: `${article.minutesToRead} min read`,
                    fr: `Lecture de ${article.minutesToRead} minutes`,
                  }[locale]
                }
              </p>
            </div>
            <Authors authorData={article.authorData} isGROQ={isGROQ} />
          </div>
          {typeof image === 'string' ? (
            <img alt={article.imgAltText} src={image} srcSet={srcSet} />
          ) : (
            <GatsbyImage alt={article.imgAltText} image={image.gatsbyImageData} className="rounded-16" />
          )}
        </div>
      </Container>
    </Section>
  );
};
